import React, { useState, useMemo, useCallback } from "react";
import "./App.css";

const ACCESS_CODE = "AGRI2026";

const REGIONS = [
  { name: "Northern Region", farmers: 42000, crop: "Maize", avgYield: 2.1, premium: 3.5 },
  { name: "Upper East", farmers: 28000, crop: "Soybean", avgYield: 1.4, premium: 4.2 },
  { name: "Ashanti Region", farmers: 35000, crop: "Rice", avgYield: 3.8, premium: 3.0 },
  { name: "Volta / Oti", farmers: 22000, crop: "Tomato", avgYield: 12.5, premium: 5.1 },
  { name: "Bono East", farmers: 18000, crop: "Maize", avgYield: 2.4, premium: 3.2 },
];

const SHOCK_SCENARIOS = [
  { name: "Normal Season", severity: 0, color: "#22c55e", icon: "\u2600\uFE0F" },
  { name: "Moderate Drought", severity: 0.25, color: "#f59e0b", icon: "\uD83C\uDF24\uFE0F" },
  { name: "Severe Drought", severity: 0.50, color: "#ef4444", icon: "\uD83D\uDD25" },
  { name: "Flooding Event", severity: 0.40, color: "#3b82f6", icon: "\uD83C\uDF0A" },
];

function formatUSD(n) {
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}
function formatNum(n) {
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(0)}K`;
  return n.toFixed(0);
}

function LoginScreen({ onLogin }) {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => {
      if (code.trim().toUpperCase() === ACCESS_CODE) { onLogin(); }
      else { setError("Invalid access code. Please check and try again."); setLoading(false); }
    }, 800);
  }, [code, onLogin]);

  return (
    <div className="login-bg">
      <div className="login-card">
        <div className="login-badge">PRIVATE ACCESS</div>
        <div className="login-logo-row"><div className="login-dot" /><span className="login-brand">Orbotrice Institute</span></div>
        <h1 className="login-title">Farmer Portfolio Risk<br />& Insurance Simulator</h1>
        <p className="login-subtitle">An interactive tool exploring how embedded insurance protects smallholder farmers, programme investments, and stakeholder confidence under different climate scenarios.</p>
        <div className="login-divider" />
        <form onSubmit={handleSubmit} className="login-form">
          <input type="text" value={code} onChange={e => { setCode(e.target.value); setError(""); }}
            placeholder="Enter access code" className="login-input" autoFocus autoComplete="off" />
          {error && <div className="login-error">{error}</div>}
          <button type="submit" className="login-btn" disabled={loading || !code.trim()}>
            {loading ? "Verifying..." : "Access Simulator"}
          </button>
        </form>
        <p className="login-footer-text">If you have not received an access code, please contact<br />Dr. Kojo Botchey — Orbotrice Institute, Zurich</p>
      </div>
    </div>
  );
}

function BarChart({ data, maxVal, label, color }) {
  return (
    <div className="barchart">
      <div className="barchart-label">{label}</div>
      {data.map((d, i) => {
        const pct = maxVal > 0 ? (d.value / maxVal) * 100 : 0;
        return (
          <div key={i} className="barchart-row">
            <div className="barchart-name">{d.label}</div>
            <div className="barchart-track">
              <div className="barchart-fill" style={{ width: `${Math.max(pct, 2)}%`, background: color, boxShadow: `0 0 12px ${color}44` }} />
            </div>
            <div className="barchart-val">{typeof d.value === "number" && d.value >= 1e3 ? formatNum(d.value) : d.value}</div>
          </div>
        );
      })}
    </div>
  );
}

function MetricCard({ label, value, sub, accent = "#22c55e" }) {
  return (
    <div className="metric-card" style={{ borderColor: `${accent}33`, boxShadow: `0 0 20px ${accent}11` }}>
      <div className="metric-label">{label}</div>
      <div className="metric-value" style={{ color: accent }}>{value}</div>
      {sub && <div className="metric-sub">{sub}</div>}
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [selectedScenario, setSelectedScenario] = useState(0);
  const [insuranceCoverage, setInsuranceCoverage] = useState(30);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const scenario = SHOCK_SCENARIOS[selectedScenario];
  const totalFarmers = REGIONS.reduce((s, r) => s + r.farmers, 0);
  const insuredFarmers = Math.round(totalFarmers * insuranceCoverage / 100);
  const uninsuredFarmers = totalFarmers - insuredFarmers;

  const results = useMemo(() => {
    const avgFarmValue = 850;
    const totalPortfolioValue = totalFarmers * avgFarmValue;
    const yieldLoss = scenario.severity;
    const uninsuredLoss = totalPortfolioValue * yieldLoss * (1 - insuranceCoverage / 100);
    const insuredLoss = totalPortfolioValue * yieldLoss * (insuranceCoverage / 100);
    const insurancePayout = insuredLoss * 0.75;
    const netLossInsured = insuredLoss - insurancePayout;
    const totalNetLoss = uninsuredLoss + netLossInsured;
    const totalGrossLoss = totalPortfolioValue * yieldLoss;
    const annualPremium = insuredFarmers * 3.8;
    const farmersDropOut_noInsurance = Math.round(totalFarmers * yieldLoss * 0.35);
    const farmersDropOut_withInsurance = Math.round(uninsuredFarmers * yieldLoss * 0.35);
    const farmersSaved = farmersDropOut_noInsurance - farmersDropOut_withInsurance;
    const programmeAtRisk_noInsurance = totalPortfolioValue * yieldLoss;
    const regionResults = REGIONS.map(r => {
      const regionInsured = Math.round(r.farmers * insuranceCoverage / 100);
      const regionValue = r.farmers * avgFarmValue;
      const regionLoss = regionValue * yieldLoss;
      const regionPayout = regionLoss * (insuranceCoverage / 100) * 0.75;
      return { ...r, insuredCount: regionInsured, grossLoss: regionLoss, payout: regionPayout, netLoss: regionLoss - regionPayout };
    });
    return { totalPortfolioValue, yieldLoss, uninsuredLoss, insurancePayout, netLossInsured, totalNetLoss, totalGrossLoss, annualPremium, farmersDropOut_noInsurance, farmersDropOut_withInsurance, farmersSaved, programmeAtRisk_noInsurance, regionResults };
  }, [selectedScenario, insuranceCoverage, totalFarmers, insuredFarmers, uninsuredFarmers, scenario]);

  const lossReduction = results.totalGrossLoss > 0 ? ((1 - results.totalNetLoss / results.totalGrossLoss) * 100).toFixed(0) : 0;

  return (
    <div className="dashboard">
      <div className="dash-header">
        <div className="dash-header-inner">
          <div className="dash-header-top">
            <div className="dash-header-badge"><div className="dash-dot" /><span>Orbotrice Institute</span></div>
            <button className="logout-btn" onClick={onLogout}>Log out</button>
          </div>
          <h1 className="dash-title">Farmer Portfolio Risk & Insurance Simulator</h1>
          <p className="dash-desc">Explore how embedding insurance into agricultural programmes protects farmers, programme investments, and stakeholder confidence under different climate scenarios.</p>
        </div>
      </div>
      <div className="dash-content">
        <div className="controls-grid">
          <div className="panel">
            <div className="panel-label">Climate Scenario</div>
            <div className="scenario-list">
              {SHOCK_SCENARIOS.map((s, i) => (
                <button key={i} onClick={() => setSelectedScenario(i)}
                  className={`scenario-btn ${selectedScenario === i ? "active" : ""}`}
                  style={selectedScenario === i ? { background: `${s.color}18`, borderColor: `${s.color}66` } : {}}>
                  <span className="scenario-icon">{s.icon}</span>
                  <div>
                    <div className="scenario-name" style={{ color: selectedScenario === i ? s.color : "#94a3b8" }}>{s.name}</div>
                    <div className="scenario-detail">{s.severity === 0 ? "No yield loss" : `${(s.severity * 100).toFixed(0)}% yield loss`}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
          <div className="panel">
            <div className="panel-label">Insurance Coverage Level</div>
            <div className="coverage-display">
              <div className="coverage-pct">{insuranceCoverage}%</div>
              <div className="coverage-sub">of {formatNum(totalFarmers)} farmers insured</div>
            </div>
            <input type="range" min={0} max={80} step={5} value={insuranceCoverage}
              onChange={e => setInsuranceCoverage(Number(e.target.value))} className="slider" />
            <div className="slider-labels"><span>0% (no insurance)</span><span>80% (high coverage)</span></div>
            <div className="coverage-split">
              <div><div className="coverage-split-label">Insured</div><div className="coverage-split-val green">{formatNum(insuredFarmers)}</div></div>
              <div><div className="coverage-split-label">Uninsured</div><div className="coverage-split-val red">{formatNum(uninsuredFarmers)}</div></div>
            </div>
            <div className="premium-box">
              <div className="premium-label">Annual Premium Cost (est.)</div>
              <div className="premium-val">{formatUSD(results.annualPremium)}</div>
              <div className="premium-sub">~$3.8 per farmer/season • embeddable in input financing</div>
            </div>
          </div>
        </div>

        {scenario.severity > 0 && (
          <div className="impact-banner" style={{ background: `linear-gradient(135deg, ${scenario.color}11 0%, #0f172a 100%)`, borderColor: `${scenario.color}33` }}>
            <div className="impact-intro">Under a <strong style={{ color: scenario.color }}>{scenario.name}</strong> scenario with {insuranceCoverage}% coverage:</div>
            <div className="impact-stats">
              <div><div className="impact-num">{formatNum(results.farmersSaved)}</div><div className="impact-label">farmers kept in production</div></div>
              <div><div className="impact-num">{formatUSD(results.insurancePayout)}</div><div className="impact-label">flows back to farmer households</div></div>
              <div><div className="impact-num">{lossReduction}%</div><div className="impact-label">of programme loss absorbed</div></div>
            </div>
          </div>
        )}

        <div className="metrics-row">
          <MetricCard label="Portfolio Value" value={formatUSD(results.totalPortfolioValue)} sub={`${formatNum(totalFarmers)} farmers`} accent="#60a5fa" />
          <MetricCard label="Gross Loss" value={scenario.severity === 0 ? "$0" : formatUSD(results.totalGrossLoss)} sub={`${(scenario.severity * 100).toFixed(0)}% yield loss`} accent={scenario.color} />
          <MetricCard label="Insurance Payout" value={formatUSD(results.insurancePayout)} sub="to insured farmers" accent="#22c55e" />
          <MetricCard label="Net Programme Loss" value={formatUSD(results.totalNetLoss)} sub={scenario.severity > 0 ? `vs ${formatUSD(results.totalGrossLoss)} without insurance` : "fully protected"} accent={results.totalNetLoss > 0 ? "#f59e0b" : "#22c55e"} />
        </div>

        <div className="compare-grid">
          <div className="compare-panel red-border">
            <div className="compare-header"><div className="compare-dot red-bg" /><div className="compare-title red-text">Without Insurance</div></div>
            <div className="compare-metrics">
              <MetricCard label="Total Loss" value={formatUSD(results.programmeAtRisk_noInsurance)} accent="#ef4444" />
              <MetricCard label="Farmers Lost" value={formatNum(results.farmersDropOut_noInsurance)} sub="drop out of production" accent="#ef4444" />
            </div>
            <div className="compare-note red-note">Full loss borne by farmers and programme. No recovery mechanism. Reporting shows unmitigated risk exposure.</div>
          </div>
          <div className="compare-panel green-border">
            <div className="compare-header"><div className="compare-dot green-bg" /><div className="compare-title green-text">With {insuranceCoverage}% Insurance</div></div>
            <div className="compare-metrics">
              <MetricCard label="Net Loss" value={formatUSD(results.totalNetLoss)} accent="#22c55e" />
              <MetricCard label="Farmers Lost" value={formatNum(results.farmersDropOut_withInsurance)} sub="drop out of production" accent="#22c55e" />
            </div>
            <div className="compare-note green-note">Insurance absorbs {lossReduction}% of loss. {formatNum(results.farmersSaved)} additional farmers stay in production. Stakeholders see structured risk management.</div>
          </div>
        </div>

        <div className="panel" style={{ marginBottom: 20 }}>
          <div className="panel-label">Regional Breakdown</div>
          <div className="table-wrap">
            <table className="region-table">
              <thead><tr>{["Region", "Crop", "Farmers", "Insured", "Gross Loss", "Payout", "Net Loss"].map(h => <th key={h}>{h}</th>)}</tr></thead>
              <tbody>
                {results.regionResults.map((r, i) => (
                  <tr key={i} className={selectedRegion === i ? "row-active" : ""} onClick={() => setSelectedRegion(selectedRegion === i ? null : i)}>
                    <td className="td-name">{r.name}</td>
                    <td className="td-muted">{r.crop}</td>
                    <td className="td-mono">{formatNum(r.farmers)}</td>
                    <td className="td-mono td-green">{formatNum(r.insuredCount)}</td>
                    <td className="td-mono" style={{ color: scenario.severity > 0 ? "#fca5a5" : "#64748b" }}>{formatUSD(r.grossLoss)}</td>
                    <td className="td-mono td-green">{formatUSD(r.payout)}</td>
                    <td className="td-mono" style={{ color: r.netLoss > 0 ? "#fbbf24" : "#22c55e" }}>{formatUSD(r.netLoss)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr>
                <td className="tf-bold">TOTAL</td><td></td>
                <td className="tf-bold td-mono">{formatNum(totalFarmers)}</td>
                <td className="tf-bold td-mono td-green">{formatNum(insuredFarmers)}</td>
                <td className="tf-bold td-mono" style={{ color: scenario.severity > 0 ? "#fca5a5" : "#64748b" }}>{formatUSD(results.totalGrossLoss)}</td>
                <td className="tf-bold td-mono td-green">{formatUSD(results.insurancePayout)}</td>
                <td className="tf-bold td-mono" style={{ color: "#fbbf24" }}>{formatUSD(results.totalNetLoss)}</td>
              </tr></tfoot>
            </table>
          </div>
        </div>

        <div className="bars-grid">
          <div className="panel"><BarChart label="Gross Loss by Region" data={results.regionResults.map(r => ({ label: r.name.split(" ")[0], value: r.grossLoss }))} maxVal={Math.max(...results.regionResults.map(r => r.grossLoss), 1)} color={scenario.color} /></div>
          <div className="panel"><BarChart label="Insurance Payouts by Region" data={results.regionResults.map(r => ({ label: r.name.split(" ")[0], value: r.payout }))} maxVal={Math.max(...results.regionResults.map(r => r.grossLoss), 1)} color="#22c55e" /></div>
        </div>

        <div className="donor-panel">
          <div className="donor-label">Stakeholder & Board Reporting Summary</div>
          <div className="donor-grid">
            <div className="donor-card"><div className="donor-card-label">Risk Mitigation Rate</div><div className="donor-card-val">{lossReduction}%</div><div className="donor-card-sub">of climate loss absorbed by insurance</div></div>
            <div className="donor-card"><div className="donor-card-label">Cost-Benefit Ratio</div><div className="donor-card-val">{results.annualPremium > 0 && results.insurancePayout > 0 ? `1:${(results.insurancePayout / results.annualPremium).toFixed(1)}` : "\u2014"}</div><div className="donor-card-sub">premium paid vs. loss recovered</div></div>
            <div className="donor-card"><div className="donor-card-label">Programme Continuity</div><div className="donor-card-val">{totalFarmers > 0 ? ((1 - results.farmersDropOut_withInsurance / totalFarmers) * 100).toFixed(1) : 100}%</div><div className="donor-card-sub">of farmers remain in production</div></div>
          </div>
        </div>

        <div className="dash-footer">
          <div>Orbotrice Institute 2026</div>
          <div className="dash-footer-disclaimer">Illustrative model for discussion purposes. Actual product design requires scoping with programme data.</div>
        </div>
      </div>
    </div>
  );
}

function App() {
  const [authenticated, setAuthenticated] = useState(false);
  return authenticated ? <Dashboard onLogout={() => setAuthenticated(false)} /> : <LoginScreen onLogin={() => setAuthenticated(true)} />;
}

export default App;
